import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router";
import {
  useJam,
  useUpdateJam,
  useDeleteJam,
  useRegenerateJoinCode,
} from "@/hooks/api/useJams";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useConfirm } from "@/hooks/useConfirm";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function JamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const { confirmProps, confirm } = useConfirm();

  const { data: jam, isLoading, error } = useJam(id);
  const updateJam = useUpdateJam();
  const deleteJam = useDeleteJam();
  const regenerateJoinCode = useRegenerateJoinCode();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const isOwner = jam?.creatorId === user?.id;
  const canEdit = isOwner || isAdmin;

  const startEditing = () => {
    if (!jam) return;
    setEditName(jam.name);
    setEditDescription(jam.description || "");
    setIsEditing(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !editName.trim()) return;

    updateJam.mutate(
      {
        id,
        data: {
          name: editName.trim(),
          description: editDescription.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!id) return;
    confirm({
      title: "Delete Jam",
      description:
        "Are you sure you want to delete this jam? This action cannot be undone.",
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: () => {
        deleteJam.mutate(id, {
          onSuccess: () => {
            navigate("/jams");
          },
        });
      },
    });
  };

  const copyToClipboard = async (text: string, title = "Copied to clipboard") => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title });
    } catch {
      toast({ title: "Failed to copy" });
    }
  };

  const inviteLink = jam ? `${window.location.origin}/join/${jam.joinCode}` : "";

  const handleRegenerateCode = () => {
    if (!id) return;
    confirm({
      title: "Regenerate Invite Code",
      description:
        "The current code and invite link will stop working. Existing members keep access.",
      confirmLabel: "Regenerate",
      onConfirm: () => {
        regenerateJoinCode.mutate(id, {
          onSuccess: () => toast({ title: "Invite code regenerated" }),
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl">
        <p className="text-muted-foreground">Loading jam...</p>
      </div>
    );
  }

  if (error || !jam) {
    return (
      <div className="max-w-3xl">
        <div className="text-center py-12">
          <h1 className="text-xl font-medium uppercase tracking-wider text-destructive mb-4">
            Jam Not Found
          </h1>
          <p className="text-muted-foreground mb-6">
            This jam may have been deleted or you don't have access to it.
          </p>
          <Link to="/jams">
            <Button>Back to Jams</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to="/jams"
          className="text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        >
          &larr; Back to Jams
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8">
        {isEditing ? (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editName">Name</Label>
              <Input
                id="editName"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDescription">Description</Label>
              <Textarea
                id="editDescription"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                maxLength={500}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={updateJam.isPending}>
                {updateJam.isPending ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </div>
            {updateJam.isError && (
              <p className="text-sm text-destructive">
                {updateJam.error?.message || "Failed to update jam"}
              </p>
            )}
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-medium uppercase tracking-wider">{jam.name}</h1>
                {jam.description && (
                  <p className="text-muted-foreground mt-2">{jam.description}</p>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={startEditing}>
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleteJam.isPending}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
            {jam.discardedAt && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-medium">
                  This jam was deleted on {formatDate(jam.discardedAt)}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Invite code - prominent display */}
      <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-6 mb-8">
        <h2 className="text-sm font-medium uppercase tracking-wider mb-2">
          Invite Collaborators
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Share this code — collaborators can join from the plugin (JAM &rarr;
          enter code) or by opening the invite link.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <code className="bg-[#1e1e1e] border border-[#616161]/20 rounded-md px-4 py-3 text-2xl font-mono tracking-[0.35em]">
            {jam.joinCode}
          </code>
          <Button
            onClick={() =>
              copyToClipboard(jam.joinCode, "Invite code copied to clipboard")
            }
          >
            Copy Code
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              copyToClipboard(inviteLink, "Invite link copied to clipboard")
            }
          >
            Copy Link
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              className="text-muted-foreground"
              onClick={handleRegenerateCode}
              disabled={regenerateJoinCode.isPending}
            >
              {regenerateJoinCode.isPending ? "Regenerating..." : "Regenerate"}
            </Button>
          )}
        </div>
      </div>

      {/* Meta info */}
      <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-6">
        <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Details</h2>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Created</dt>
            <dd className="font-mono text-sm">{formatDate(jam.createdAt)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Last Activity</dt>
            <dd className="font-mono text-sm">{formatDate(jam.lastActivityAt)}</dd>
          </div>
          {isAdmin && (
            <div className="flex justify-between">
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Creator ID</dt>
              <dd className="font-mono text-sm">{jam.creatorId}</dd>
            </div>
          )}
        </dl>
      </div>

      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
